import type { InspirationGalleryItem } from '@/types/inspiration-gallery'

/**
 * Filters inspiration items by product while preserving randomization order
 * @param items Pre-randomized inspiration items
 * @param productSlug Product slug to filter by ('all' returns all items)
 * @returns Filtered items preserving original randomization
 */
export function filterInspirationByProduct(
  items: InspirationGalleryItem[],
  productSlug: string,
): InspirationGalleryItem[] {
  if (productSlug === 'all') {
    return items
  }

  return items.filter((item) => item.products.some((product) => product.slug === productSlug))
}

/**
 * Extracts unique products from inspiration items
 * Sorted by sortOrder first, then alphabetically by title, with 'all' as first option
 */
export function getAvailableProducts(items: InspirationGalleryItem[]) {
  const products = new Map<
    string,
    { id: string; title: string; slug: string; sortOrder?: number }
  >()

  items.forEach((item) => {
    const list = Array.isArray(item.products) ? item.products : []
    list.forEach((product) => {
      if (!products.has(product.slug)) {
        products.set(product.slug, {
          id: product.id,
          title: product.title,
          slug: product.slug,
          sortOrder: product.sortOrder,
        })
      }
    })
  })

  // Convert to array and sort by sortOrder first, then alphabetically
  const sorted = Array.from(products.values()).sort((a, b) => {
    const orderA = a.sortOrder ?? 999
    const orderB = b.sortOrder ?? 999
    if (orderA !== orderB) {
      return orderA - orderB
    }
    return a.title.localeCompare(b.title)
  })

  // Add 'all' option at the beginning
  return [{ id: 'all', title: 'Alle', slug: 'all' }, ...sorted]
}
